import type { Rng } from './rng';
import { defaultRng, createRng } from './rng';
import {
  generateEmail,
  generatePeppolParticipantId,
  generateUaePhone,
  generateUaeTrn,
} from './primitives';
import { COMPANIES, PEOPLE, type SampleCompany, type SamplePerson } from './uae';

export type ErpType =
  | 'SAP'
  | 'Oracle'
  | 'Dynamics365'
  | 'Zoho'
  | 'NetSuite'
  | 'Tally'
  | 'Other';
export type IntegrationMode = 'API' | 'Portal' | 'FileUpload';
export type MfaMethod = 'TOTP' | 'SMS' | 'Email' | 'UAE_PASS';
export type OnboardingStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'TERMINATED';

const ERPS: ErpType[] = ['SAP', 'Oracle', 'Dynamics365', 'Zoho', 'NetSuite', 'Tally', 'Other'];
const INTEGRATIONS: IntegrationMode[] = ['API', 'Portal', 'FileUpload'];
const MFAS: MfaMethod[] = ['TOTP', 'SMS', 'Email', 'UAE_PASS'];

export interface SampleTenantMetrics {
  invoicesLast30d: number;
  rejectionRate: number;
  vatCollectedAed: number;
  nextTddDeadline: string | null;
}

export interface SampleTenant {
  tenantId: string;
  companyNameEn: string;
  companyNameAr: string;
  trn: string;
  peppolParticipantId: string;
  contactName: string;
  contactNameAr: string;
  contactEmail: string;
  contactPhone: string;
  erpType: ErpType;
  integrationMode: IntegrationMode;
  mfaMethod: MfaMethod;
  onboardingStatus: OnboardingStatus;
  createdAt: string;
  activatedAt: string | null;
  metrics: SampleTenantMetrics;
}

export interface OnboardingFormSample {
  companyNameEn: string;
  companyNameAr: string;
  trn: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  mfaMethod: MfaMethod;
  erpType: ErpType;
  integrationMode: IntegrationMode;
}

function pseudoUuid(rng: Rng): string {
  const hex = (n: number) => rng.int(0, 15).toString(16).repeat(n);
  return `${hex(8)}-${hex(4)}-4${hex(3)}-a${hex(3)}-${hex(12)}`;
}

function isoMinus(days: number, hour = 9): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  d.setUTCHours(hour, 0, 0, 0);
  return d.toISOString();
}

function isoPlus(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + days);
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
}

function buildTenant(
  rng: Rng,
  company: SampleCompany,
  contactPerson: SamplePerson,
  index: number,
): SampleTenant {
  const trn = generateUaeTrn(rng);
  const status: OnboardingStatus =
    index < 3 ? 'PENDING' : index >= 19 ? (rng.chance(0.4) ? 'SUSPENDED' : 'ACTIVE') : 'ACTIVE';

  const createdDaysAgo = rng.int(7, 240);
  const activated = status === 'ACTIVE' || status === 'SUSPENDED';
  const activatedDaysAgo = activated ? Math.max(1, createdDaysAgo - rng.int(0, 5)) : null;

  // Metrics: scale invoices by integration mode (API > Portal > FileUpload),
  // sprinkle a realistic rejection rate.
  const integrationMode = rng.pick(INTEGRATIONS);
  const baseInv =
    integrationMode === 'API' ? rng.int(800, 4500) : integrationMode === 'Portal' ? rng.int(50, 700) : rng.int(20, 300);
  const invoicesLast30d = status === 'ACTIVE' ? baseInv : status === 'SUSPENDED' ? rng.int(0, 50) : 0;
  const rejectionRate = status === 'ACTIVE' ? Number((rng.next() * 0.06).toFixed(4)) : 0;
  const avgInvAed = rng.int(500, 35_000);
  const vatCollected = Math.round(invoicesLast30d * avgInvAed * 0.05);

  const nextTddDeadline =
    invoicesLast30d > 0 ? isoPlus(rng.int(2, 13)) : null;

  return {
    tenantId: pseudoUuid(rng),
    companyNameEn: company.nameEn,
    companyNameAr: company.nameAr,
    trn,
    peppolParticipantId: generatePeppolParticipantId(trn),
    contactName: `${contactPerson.givenName} ${contactPerson.familyName}`,
    contactNameAr: `${contactPerson.givenNameAr} ${contactPerson.familyNameAr}`,
    contactEmail: generateEmail(contactPerson.givenName, contactPerson.familyName, company.slug),
    contactPhone: generateUaePhone(rng),
    erpType: rng.pick(ERPS),
    integrationMode,
    mfaMethod: rng.pick(MFAS),
    onboardingStatus: status,
    createdAt: isoMinus(createdDaysAgo, rng.int(7, 18)),
    activatedAt: activatedDaysAgo == null ? null : isoMinus(activatedDaysAgo, rng.int(7, 18)),
    metrics: {
      invoicesLast30d,
      rejectionRate,
      vatCollectedAed: vatCollected,
      nextTddDeadline,
    },
  };
}

export function generateSampleTenant(rng: Rng = defaultRng()): SampleTenant {
  return buildTenant(rng, rng.pick(COMPANIES), rng.pick(PEOPLE), 5);
}

/**
 * Deterministically generate `count` tenants. Each company is used at most
 * once until COMPANIES is exhausted. Default seed gives a stable demo set.
 */
export function generateSampleTenants(count = 25, seed = 0xa1adb1): SampleTenant[] {
  const rng = createRng(seed);
  const companies = rng.sample(COMPANIES, Math.min(count, COMPANIES.length));
  const people = rng.sample(PEOPLE, Math.min(count, PEOPLE.length));
  // If `count` exceeds available companies/people, wrap with mutation suffixes.
  const tenants: SampleTenant[] = [];
  for (let i = 0; i < count; i++) {
    const company = (companies[i % companies.length] ?? COMPANIES[0]) as SampleCompany;
    const person = (people[i % people.length] ?? PEOPLE[0]) as SamplePerson;
    tenants.push(buildTenant(rng, company, person, i));
  }
  return tenants;
}

/**
 * Generate a payload that fills the onboarding wizard form.
 * Always passes the Zod TenantSchema validation.
 */
export function generateOnboardingFormSample(rng: Rng = defaultRng()): OnboardingFormSample {
  const company = rng.pick(COMPANIES);
  const person = rng.pick(PEOPLE);
  return {
    companyNameEn: company.nameEn,
    companyNameAr: company.nameAr,
    trn: generateUaeTrn(rng),
    contactName: `${person.givenName} ${person.familyName}`,
    contactEmail: generateEmail(person.givenName, person.familyName, company.slug),
    contactPhone: generateUaePhone(rng),
    mfaMethod: rng.pick(MFAS),
    erpType: rng.pick(ERPS),
    integrationMode: rng.pick(INTEGRATIONS),
  };
}
